const CHUNK_LOAD_FAILED_REGEX = /loading chunk \d+ failed/i;
const CSS_CHUNK_LOAD_FAILED_REGEX = /css chunk load failed/i;
const WEBPACK_MODULE_UNDEFINED_REGEX =
  /cannot read properties of undefined \(reading ['"](call|number|exports|default)['"]\)/i;
const FIREFOX_WEBPACK_MODULE_UNDEFINED_REGEX =
  /can't access property ['"](call|number|exports|default)['"],.*is undefined/i;
const WEBPACK_BOOTSTRAP_STACK_REGEX = /webpack:\/\/_N_E\/webpack\/bootstrap/i;
const WEBPACK_RUNTIME_CHUNK_URL_REGEX =
  /\/_next\/static\/chunks\/webpack-[^/]+\.js/i;
// Turbopack's chunk loader throws its own wording — webpack's name and
// messages never appear. Taken from the strings in a Turbopack production
// build, not from the docs.
const TURBOPACK_CHUNK_LOAD_FAILED_REGEX = /failed to load chunk/i;
const TURBOPACK_CHUNK_TYPE_REGEX = /can't infer type of chunk from url/i;
// Turbopack's page and shared chunk filenames are not build-scoped — build
// identity rides only in the `?dpl=` query, which the origin ignores. A stale
// tab asks for a filename it knows and gets the current build's bytes; module
// ids are renumbered per build, so the lookup finds no factory and throws.
const TURBOPACK_MODULE_FACTORY_MISSING_REGEX =
  /module factory is not available/i;
// The Turbopack runtime filename IS build-scoped, so it is the frame that
// pins a stack to the tab's original build.
const TURBOPACK_RUNTIME_CHUNK_URL_REGEX =
  /\/_next\/static\/chunks\/turbopack-[^/]+\.js/i;

/**
 * Detects webpack ChunkLoadError thrown when the browser fails to fetch a
 * code-split JS or CSS chunk. This almost always happens after a new
 * deployment: the user's cached HTML references old chunk filenames that no
 * longer exist on the CDN. A page reload resolves the issue because the
 * browser fetches the new HTML with updated chunk references.
 *
 * Covers both bundlers. Turbopack does not set `ChunkLoadError` and does not
 * use webpack's wording, so a webpack-only matcher silently stops recovering
 * stale tabs the moment the build switches.
 *
 * Matches by:
 * - error.name === "ChunkLoadError"  (webpack sets this)
 * - error.message / string containing "Loading chunk … failed"
 * - error.message / string containing "CSS chunk load failed"
 * - error.message containing "Failed to load chunk", "can't infer type of
 *   chunk from URL", or "module factory is not available" (Turbopack)
 * - error.message matching a webpack bootstrap TypeError accessing a known
 *   webpack-internal property ('call', 'number', 'exports', 'default') on
 *   undefined with a matching stack trace (cross-deployment module-id mismatch)
 * - stack mixing the webpack or Turbopack runtime with two or more distinct
 *   Vercel `dpl=` IDs (stale runtime executing a newer page/shared chunk)
 */
export function isChunkLoadError(input: {
  name?: string;
  message?: string;
  kind?: string;
  stack?: string;
}): boolean {
  const name = (input.name ?? "").toLowerCase();
  const message = input.message ?? "";
  const kind = (input.kind ?? "").toLowerCase();

  if (name === "chunkloaderror" || kind === "chunkloaderror") {
    return true;
  }

  if (
    CHUNK_LOAD_FAILED_REGEX.test(message) ||
    CSS_CHUNK_LOAD_FAILED_REGEX.test(message) ||
    TURBOPACK_CHUNK_LOAD_FAILED_REGEX.test(message) ||
    TURBOPACK_CHUNK_TYPE_REGEX.test(message) ||
    TURBOPACK_MODULE_FACTORY_MISSING_REGEX.test(message)
  ) {
    return true;
  }

  const stack = input.stack ?? "";
  const hasBundlerRuntimeStack = _hasBundlerRuntimeStack(stack);

  if (
    (WEBPACK_MODULE_UNDEFINED_REGEX.test(message) ||
      FIREFOX_WEBPACK_MODULE_UNDEFINED_REGEX.test(message)) &&
    hasBundlerRuntimeStack
  ) {
    return true;
  }

  // Application-property TypeErrors (e.g. reading 'map') are real bugs when
  // every frame is from one deploy. Mixed `dpl=` hashes mean the stale
  // bundler runtime is executing a newer module whose exports don't exist
  // on the already-cached sibling chunk — same class of failure as above.
  if (hasBundlerRuntimeStack && _hasMismatchedDeploymentIds(stack)) {
    return true;
  }

  return false;
}

function _hasBundlerRuntimeStack(stack: string): boolean {
  return (
    WEBPACK_BOOTSTRAP_STACK_REGEX.test(stack) ||
    WEBPACK_RUNTIME_CHUNK_URL_REGEX.test(stack) ||
    TURBOPACK_RUNTIME_CHUNK_URL_REGEX.test(stack)
  );
}

function _hasMismatchedDeploymentIds(stack: string): boolean {
  const ids = new Set<string>();
  const regex = /[?&]dpl=([a-zA-Z0-9]+)/g;
  for (const match of stack.matchAll(regex)) {
    ids.add(match[1]);
    if (ids.size > 1) {
      return true;
    }
  }
  return false;
}
